<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { FormKit } from '@formkit/vue';
import { useBranding } from 'shared/composables/useBranding';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import BitrixConnectionGuide from './BitrixConnectionGuide.vue';
import NemesisEntryInfo from './NemesisEntryInfo.vue';
import {
  generateNemesisToken,
  NEMESIS_OPTIONAL_GROUPS,
  NEMESIS_OPTION_SECTIONS,
  nemesisGroupOfField,
  nemesisCustomizedGroups,
  nemesisFieldReset,
} from 'dashboard/helper/nemesisEntryHelper';
import BitrixSurveyButtonsConfig from '../inbox/settingsPage/BitrixSurveyButtonsConfig.vue';
import { templateHasButtonsComponent } from 'dashboard/helper/templateHelper';
import {
  buildBitrixSurveyButtonsPayload,
  invalidBitrixSurveyButtonRows,
} from 'dashboard/helper/bitrixSurveyButtonsHelper';

// entity/status_field/status_field_shared/survey_buttons only make sense once a WhatsApp
// template with actual reply buttons is selected (rendered instead by
// <BitrixSurveyButtonsConfig>, the same 2-tab widget used when editing an already-connected
// hook from Settings > Inboxes > [inbox] > Integrations — see that component and
// InboxIntegrationsPage.vue). status_contactado/status_no_contactado are the old plain-text
// SI/NO fields, fully replaced by survey_buttons: a template with no buttons (e.g. the sandbox
// `hello_world`) isn't expected to get a real survey reply at all, so none of these are shown or
// required for it (Integrations::Hook#validate_bitrix_settings only requires anything when
// `survey_buttons` is configured — see HIDDEN_HINT below for what's shown instead).
const BITRIX_MANAGED_FIELD_NAMES = [
  'entity',
  'status_field_shared',
  'status_field',
  'survey_buttons',
  'status_contactado',
  'status_no_contactado',
];

// API Nemesis: cada cuántos milisegundos se refresca la lista de integraciones mientras se espera la
// primera conexión (ver startConnectionWatch).
const CONNECTION_REFRESH_MS = 5000;

export default {
  components: {
    FormKit,
    NextButton,
    Banner,
    BitrixConnectionGuide,
    BitrixSurveyButtonsConfig,
    NemesisEntryInfo,
  },
  props: {
    integrationId: {
      type: String,
      required: true,
    },
    // Hook ya existente a editar (solo para integraciones con `editable_from_list`, ver apps.yml).
    // Sin esto, el modal crea una integración nueva.
    hook: {
      type: Object,
      default: null,
    },
  },
  emits: ['close'],
  setup(props) {
    const { integration, isHookTypeInbox } = useIntegrationHook(
      props.integrationId
    );
    const { replaceInstallationName } = useBranding();

    return { integration, isHookTypeInbox, replaceInstallationName };
  },
  data() {
    return {
      endPoint: '',
      alertMessage: '',
      values: {},
      // Kept separate from `values` on purpose — see the comment in
      // BitrixSurveyButtonsConfig.vue's props declaration for why survey_buttons can't live
      // inside the FormKit-managed `values` object.
      surveyButtonRows: [],
      // API Nemesis: ids de los grupos de campos opcionales que se agregaron con un botón, en el
      // orden en que se agregaron (ver NEMESIS_OPTIONAL_GROUPS).
      openGroups: [],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'integrations/getUIFlags',
      dialogFlowEnabledInboxes: 'inboxes/dialogFlowEnabledInboxes',
      agents: 'agents/getAgents',
      teams: 'teams/getTeams',
    }),
    inboxes() {
      return this.dialogFlowEnabledInboxes
        .filter(inbox => {
          if (!this.isIntegrationDialogflow) {
            return true;
          }
          return !this.connectedDialogflowInboxIds.includes(inbox.id);
        })
        .map(inbox => ({ label: inbox.name, value: inbox.id }));
    },

    connectedDialogflowInboxIds() {
      if (!this.isIntegrationDialogflow) {
        return [];
      }
      return this.integration.hooks.map(hook => hook.inbox?.id);
    },
    formItems() {
      return this.integration.settings_form_schema;
    },
    isIntegrationDialogflow() {
      return this.integration.id === 'dialogflow';
    },
    isBitrix() {
      return this.integration.id === 'bitrix';
    },
    isNemesis() {
      return this.integration.id === 'nemesis';
    },
    isEditing() {
      return !!this.hook;
    },
    // La integración que se está editando, siempre con lo último que hay en el store (para ver cuándo
    // se conecta quien manda los casos sin cerrar la pantalla). Sin store, la que llegó por prop.
    currentHook() {
      if (!this.hook) return null;
      const hooks = this.integration.hooks || [];
      return (
        hooks.find(candidate => candidate.id === this.hook.id) || this.hook
      );
    },
    whatsappInboxes() {
      return this.$store.getters['inboxes/getWhatsAppInboxes'] || [];
    },
    // API Nemesis: bandejas de WhatsApp de la cuenta y, de la bandeja elegida, sus plantillas
    // aprobadas (las plantillas dependen de la bandeja, a diferencia del alta de Bitrix).
    nemesisInboxOptions() {
      return this.whatsappInboxes.map(inbox => ({
        label: inbox.name,
        value: inbox.id,
      }));
    },
    nemesisTemplateOptions() {
      if (!this.values.linked_inbox_id) return [];
      const templates =
        this.$store.getters['inboxes/getFilteredWhatsAppTemplates'](
          this.values.linked_inbox_id
        ) || [];
      return [...new Set(templates.map(template => template.name))].map(
        name => ({ label: name, value: name })
      );
    },
    // El mismo formulario del backend (apps.yml), pero con los selectores completados. `section`
    // sigue en cada item para agrupar; se saca recién al dibujar cada campo.
    nemesisFormItems() {
      return this.formItems.map(item => {
        if (item.name === 'linked_inbox_id') {
          return { ...item, options: this.nemesisInboxOptions };
        }
        if (item.name === 'template_name') {
          return {
            ...item,
            options: this.nemesisTemplateOptions,
            placeholder: this.values.linked_inbox_id
              ? item.placeholder
              : this.$t('INTEGRATION_APPS.NEMESIS.SELECT_INBOX_FIRST'),
          };
        }
        if (item.name === 'assigned_agent_id') {
          return { ...item, options: this.agentOptions };
        }
        if (item.name === 'assigned_team_id') {
          return { ...item, options: this.teamOptions };
        }
        return item;
      });
    },
    // Solo lo indispensable (nombre, token, bandeja y plantilla). El resto de los campos se suma con
    // los botones de "Agregar" (ver nemesisOptionalGroups).
    nemesisSections() {
      const sections = [];
      this.nemesisFormItems.forEach(({ section, ...item }) => {
        if (nemesisGroupOfField(item.name)) return;
        let group = sections.find(candidate => candidate.title === section);
        if (!group) {
          group = { title: section, items: [] };
          sections.push(group);
        }
        group.items.push(item);
      });
      return sections;
    },
    // Todos los grupos opcionales se dibujan siempre (los cerrados con `v-show`): así sus campos
    // siguen en el formulario con su valor por defecto aunque no se hayan abierto.
    nemesisOptionalGroups() {
      return NEMESIS_OPTIONAL_GROUPS.map(group => ({
        ...group,
        items: group.fields
          .map(name => this.nemesisFormItems.find(item => item.name === name))
          .filter(Boolean)
          .map(({ section, ...item }) => item),
      })).filter(group => group.items.length);
    },
    nemesisClosedGroups() {
      return this.nemesisOptionalGroups.filter(
        group => !this.openGroups.includes(group.id)
      );
    },
    // Las opciones que todavía no se agregaron, ordenadas en bloques ("¿Quién atiende?", "¿Qué datos
    // manda el sistema?", ...); un bloque sin opciones pendientes no se muestra.
    nemesisClosedSections() {
      return NEMESIS_OPTION_SECTIONS.map(key => ({
        key,
        groups: this.nemesisClosedGroups.filter(group => group.section === key),
      })).filter(section => section.groups.length);
    },
    tokenI18nPrefix() {
      return this.isNemesis ? 'NEMESIS' : 'BITRIX';
    },
    // Note: the survey_result/SI and survey_result/NO webhook routes still exist
    // server-side (called by a Bitrix Automation Rule, not by Chatwoot), but we
    // don't surface them here — only the lead-creation URL needs to be handed to
    // Bitrix to set this integration up.
    bitrixUrlItem() {
      return {
        label: this.$t('INTEGRATION_APPS.BITRIX.URLS.LEADS_LABEL'),
        value: `${window.location.origin}/webhooks/bitrix/leads`,
      };
    },
    bitrixBodyExample() {
      return JSON.stringify(
        {
          name: 'Juan Pérez',
          phone: '5491122334455',
          email: 'juan@example.com',
          bitrix_id: '12345',
          deal_id: '98765',
        },
        null,
        2
      );
    },
    bridgeTokenItem() {
      return this.formItems.find(item => item.name === 'bridge_token');
    },
    // Active WhatsApp templates across every WhatsApp inbox in the account — at this point
    // (creating the hook) it isn't linked to a specific inbox yet, so we can't narrow it down
    // to just one; the inbox-scoped "Integrations" tab lets you re-pick from a single inbox
    // later, once linked.
    templateOptions() {
      const whatsappInboxes =
        this.$store.getters['inboxes/getWhatsAppInboxes'] || [];
      const names = new Set();
      whatsappInboxes.forEach(inbox => {
        const templates = this.$store.getters[
          'inboxes/getFilteredWhatsAppTemplates'
        ](inbox.id);
        (templates || []).forEach(template => names.add(template.name));
      });
      return [...names].map(name => ({ label: name, value: name }));
    },
    agentOptions() {
      return (this.agents || []).map(agent => ({
        label: agent.name,
        value: agent.id,
      }));
    },
    teamOptions() {
      return (this.teams || []).map(team => ({
        label: team.name,
        value: team.id,
      }));
    },
    visibleFormItems() {
      const items = this.isBitrix
        ? this.formItems.filter(
            item =>
              item.name !== 'bridge_token' &&
              !BITRIX_MANAGED_FIELD_NAMES.includes(item.name)
          )
        : this.formItems;

      return items.map(item => {
        if (item.name === 'survey_template_name') {
          return {
            ...item,
            type: 'select',
            options: this.templateOptions,
            // Sin placeholder, FormKit selecciona la primera plantilla de la lista sola
            // cuando el valor todavía es undefined (hook nuevo) -- bug real encontrado en la
            // revisión de esta sesión, mismo criterio que ya usaba InboxIntegrationsPage.vue.
            placeholder: this.$t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.TEMPLATE_FIELD.PLACEHOLDER'
            ),
          };
        }
        if (item.name === 'assigned_agent_id') {
          return { ...item, type: 'select', options: this.agentOptions };
        }
        if (item.name === 'assigned_team_id') {
          return { ...item, type: 'select', options: this.teamOptions };
        }
        return item;
      });
    },
    // Handed down to BitrixSurveyButtonsConfig so it can render entity/status_field with the
    // same label/help copy apps.yml already defines for them.
    bitrixEntityField() {
      return this.formItems.find(item => item.name === 'entity');
    },
    bitrixStatusFieldField() {
      return this.formItems.find(item => item.name === 'status_field');
    },
    // Unlike the inbox-scoped edit page, this create flow isn't tied to one inbox — the admin
    // picks `linked_inbox_id` in this very form (see the `inbox`/`linked_inbox_id` field
    // rendered below). Once they have, resolve the template from that inbox specifically
    // (matching name + language, same as the edit page); until then, fall back to a name-only
    // match against any WhatsApp inbox's templates, so the widget can still appear while the
    // admin is filling the form top-to-bottom.
    selectedBitrixTemplate() {
      if (!this.isBitrix || !this.values.survey_template_name) return null;

      const candidateInboxes = this.values.linked_inbox_id
        ? [{ id: this.values.linked_inbox_id }]
        : this.$store.getters['inboxes/getWhatsAppInboxes'] || [];

      const templates = candidateInboxes.flatMap(
        inbox =>
          this.$store.getters['inboxes/getFilteredWhatsAppTemplates'](
            inbox.id
          ) || []
      );

      const byNameAndLanguage = templates.find(
        template =>
          template.name === this.values.survey_template_name &&
          (!this.values.survey_template_language ||
            template.language === this.values.survey_template_language)
      );
      return (
        byNameAndLanguage ||
        templates.find(
          template => template.name === this.values.survey_template_name
        ) ||
        null
      );
    },
    // Hidden (not cleared) once a buttons-template is picked and then swapped for a
    // no-buttons one — matches InboxIntegrationsPage.vue's behavior.
    showBitrixSurveyWidget() {
      return (
        this.isBitrix &&
        !!this.selectedBitrixTemplate &&
        templateHasButtonsComponent(this.selectedBitrixTemplate)
      );
    },
    submitButtonLabel() {
      if (this.integration.id === 'openai' && this.uiFlags.isCreatingHook) {
        return this.$t('INTEGRATION_APPS.ADD.FORM.VALIDATING_OPENAI');
      }

      if (this.isEditing) {
        return this.$t('INTEGRATION_APPS.EDIT.SUBMIT');
      }

      return this.$t('INTEGRATION_APPS.ADD.FORM.SUBMIT');
    },
    isSubmitting() {
      return this.isEditing
        ? this.uiFlags.isUpdatingHook
        : this.uiFlags.isCreatingHook;
    },
  },
  watch: {
    // Si se cambia la bandeja, la plantilla elegida puede no existir en la nueva: se limpia.
    'values.linked_inbox_id'(newInboxId, oldInboxId) {
      if (!this.isNemesis || !oldInboxId || newInboxId === oldInboxId) return;
      const stillValid = this.nemesisTemplateOptions.some(
        option => option.value === this.values.template_name
      );
      if (!stillValid) {
        this.values = { ...this.values, template_name: undefined };
      }
    },
  },
  created() {
    if (!this.isNemesis) return;
    if (this.isEditing) {
      this.values = { ...this.hook.settings };
      // Al editar, quedan abiertos los grupos que ya tienen algo distinto de lo estándar.
      this.openGroups = nemesisCustomizedGroups(this.values, this.formItems);
      return;
    }
    // Alta: valores por defecto del formulario (rutas del body, idioma, dominios) y un token ya
    // generado, para que alcance con elegir bandeja, plantilla y equipo.
    const defaults = {};
    this.formItems.forEach(item => {
      if (item.default !== undefined) defaults[item.name] = item.default;
    });
    this.values = { ...defaults, bridge_token: generateNemesisToken() };
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('teams/get');
    this.startConnectionWatch();
  },
  beforeUnmount() {
    this.stopConnectionWatch();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    generateBridgeToken() {
      this.values = { ...this.values, bridge_token: generateNemesisToken() };
    },
    async copyBridgeToken() {
      if (!this.values.bridge_token) {
        useAlert(
          this.$t(`INTEGRATION_APPS.${this.tokenI18nPrefix}.TOKEN.EMPTY`)
        );
        return;
      }
      await copyTextToClipboard(this.values.bridge_token);
      useAlert(
        this.$t(`INTEGRATION_APPS.${this.tokenI18nPrefix}.TOKEN.COPY_SUCCESS`)
      );
    },
    // API Nemesis, al editar: mientras quien manda los casos no se conectó (o todavía no mandó un body),
    // se refresca en silencio cada tanto para mostrarlo apenas llega, sin tener que recargar la página.
    startConnectionWatch() {
      if (!this.isNemesis || !this.isEditing || this.connectionComplete())
        return;
      this.connectionTimer = setInterval(() => {
        if (this.connectionComplete()) {
          this.stopConnectionWatch();
          return;
        }
        this.$store.dispatch('integrations/refresh');
      }, CONNECTION_REFRESH_MS);
    },
    stopConnectionWatch() {
      clearInterval(this.connectionTimer);
      this.connectionTimer = null;
    },
    connectionComplete() {
      const settings = this.currentHook?.settings || {};
      return !!(settings.connected_at && settings.sample_body);
    },
    // "Volver a capturar": borra la conexión y el ejemplo guardados; el próximo pedido con el token los
    // vuelve a llenar.
    async resetConnectionSample() {
      try {
        await this.$store.dispatch('integrations/updateHook', {
          hookId: this.hook.id,
          settings: { connected_at: null, sample_body: null },
        });
        await this.$store.dispatch('integrations/refresh');
        this.stopConnectionWatch();
        this.startConnectionWatch();
        useAlert(
          this.$t('INTEGRATION_APPS.NEMESIS.CONNECTION.RECAPTURE_SUCCESS')
        );
      } catch (error) {
        useAlert(
          this.$t('INTEGRATION_APPS.NEMESIS.CONNECTION.RECAPTURE_ERROR')
        );
      }
    },
    isGroupOpen(groupId) {
      return this.openGroups.includes(groupId);
    },
    openGroup(groupId) {
      if (!this.isGroupOpen(groupId)) {
        this.openGroups = [...this.openGroups, groupId];
      }
    },
    // Quitar un grupo lo cierra y devuelve sus campos a lo estándar (por defecto o vacío).
    closeGroup(groupId) {
      const group = this.nemesisOptionalGroups.find(
        candidate => candidate.id === groupId
      );
      if (!group) return;
      const reset = {};
      group.items.forEach(item => {
        reset[item.name] = nemesisFieldReset(item);
      });
      this.values = { ...this.values, ...reset };
      this.openGroups = this.openGroups.filter(id => id !== groupId);
    },
    goToWhatsappSetup() {
      this.$router.push({ name: 'settings_inbox_new' });
    },
    async copyBitrixUrl(value) {
      await copyTextToClipboard(value);
      useAlert(this.$t('INTEGRATION_APPS.BITRIX.URLS.COPY_SUCCESS'));
    },
    buildHookPayload() {
      const hookPayload = {
        app_id: this.integration.id,
        settings: {},
      };

      hookPayload.settings = Object.keys(this.values).reduce((acc, key) => {
        if (key !== 'inbox') {
          acc[key] = this.values[key];
        }
        return acc;
      }, {});

      if (this.isNemesis) {
        // Los completa el servidor: no se mandan de vuelta con lo que se vio al abrir la pantalla (podría
        // pisar una conexión que llegó mientras tanto).
        delete hookPayload.settings.connected_at;
        delete hookPayload.settings.sample_body;
        // Con un token nuevo, quien manda los casos tiene que volver a conectarse: se borra la conexión
        // anterior para que se vea la nueva.
        if (this.isEditing && this.values.bridge_token) {
          hookPayload.settings.connected_at = null;
          hookPayload.settings.sample_body = null;
        }
      }

      this.formItems.forEach(item => {
        if (item.validation?.includes('JSON')) {
          hookPayload.settings[item.name] = JSON.parse(
            hookPayload.settings[item.name]
          );
        }
      });

      if (this.isBitrix) {
        if (this.showBitrixSurveyWidget) {
          const payload = buildBitrixSurveyButtonsPayload(
            this.surveyButtonRows
          );
          if (payload) {
            hookPayload.settings.survey_buttons = payload;
            // No shared/global toggle in this UI — every button always carries its own
            // status_field (see BitrixSurveyButtonsConfig).
            hookPayload.settings.status_field_shared = false;
          } else {
            delete hookPayload.settings.survey_buttons;
          }
        } else {
          delete hookPayload.settings.survey_buttons;
        }
      }

      if (this.isHookTypeInbox && this.values.inbox) {
        hookPayload.inbox_id = this.values.inbox;
      }

      return hookPayload;
    },
    async submitForm() {
      // Mismo chequeo que InboxIntegrationsPage.vue: los campos del widget de botones van con
      // `ignore`, así que el form nativo no los valida solo.
      if (this.isBitrix && this.showBitrixSurveyWidget) {
        const incompleteRows = invalidBitrixSurveyButtonRows(
          this.surveyButtonRows
        );
        if (incompleteRows.length) {
          useAlert(
            this.$t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.INCOMPLETE_ROWS_ERROR',
              { rows: incompleteRows.join(', ') }
            )
          );
          return;
        }
      }

      try {
        const payload = this.buildHookPayload();
        if (this.isEditing) {
          await this.$store.dispatch('integrations/updateHook', {
            hookId: this.hook.id,
            settings: payload.settings,
          });
          await this.$store.dispatch('integrations/get');
        } else {
          await this.$store.dispatch('integrations/createHook', payload);
        }
        this.alertMessage = this.$t(
          this.isEditing
            ? 'INTEGRATION_APPS.EDIT.API.SUCCESS_MESSAGE'
            : 'INTEGRATION_APPS.ADD.API.SUCCESS_MESSAGE'
        );
        this.onClose();
      } catch (error) {
        const errorMessage = error?.response?.data?.message;
        this.alertMessage =
          errorMessage ||
          this.$t(
            this.isEditing
              ? 'INTEGRATION_APPS.EDIT.API.ERROR_MESSAGE'
              : 'INTEGRATION_APPS.ADD.API.ERROR_MESSAGE'
          );
      } finally {
        useAlert(this.alertMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto integration-hooks">
    <woot-modal-header
      :header-title="integration.name"
      :header-content="replaceInstallationName(integration.short_description)"
    />
    <div v-if="isBitrix" class="flex flex-col gap-4 mb-4 px-8">
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-1.5">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ $t('INTEGRATION_APPS.BITRIX.URLS.TITLE') }}
          </h4>
          <i
            v-tooltip.top="$t('INTEGRATION_APPS.BITRIX.URLS.TOOLTIP')"
            class="i-lucide-info text-n-slate-10 w-3.5 h-3.5 cursor-help"
          />
        </div>
        <section
          class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12"
        >
          <div class="ps-3 pe-1 py-2 flex justify-between items-center gap-2">
            <div class="flex flex-col min-w-0">
              <div class="text-n-slate-11 text-xs">
                {{ bitrixUrlItem.label }}
              </div>
              <div class="truncate">{{ bitrixUrlItem.value }}</div>
            </div>
            <NextButton
              type="button"
              ghost
              sm
              slate
              icon="i-lucide-copy"
              @click="copyBitrixUrl(bitrixUrlItem.value)"
            />
          </div>
        </section>
        <p class="text-xs text-n-slate-11">
          {{ $t('INTEGRATION_APPS.BITRIX.URLS.AUTH_NOTE') }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ $t('INTEGRATION_APPS.BITRIX.BODY_EXAMPLE.TITLE') }}
        </h4>
        <pre
          class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-xs text-n-slate-12 p-3 overflow-x-auto"
        ><code>{{ bitrixBodyExample }}</code></pre>
      </div>
    </div>
    <BitrixConnectionGuide v-if="isBitrix" />
    <div v-if="isNemesis" class="flex flex-col gap-4 px-8">
      <Banner
        :color="whatsappInboxes.length ? 'teal' : 'amber'"
        :action-label="
          whatsappInboxes.length
            ? null
            : $t('INTEGRATION_APPS.NEMESIS.WHATSAPP_LINK.CTA')
        "
        @action="goToWhatsappSetup"
      >
        {{
          whatsappInboxes.length
            ? $t('INTEGRATION_APPS.NEMESIS.WHATSAPP_LINK.CONNECTED')
            : $t('INTEGRATION_APPS.NEMESIS.WHATSAPP_LINK.MISSING')
        }}
      </Banner>
      <NemesisEntryInfo :hook="currentHook" @reset="resetConnectionSample" />
    </div>
    <FormKit
      v-model="values"
      type="form"
      form-class="w-full grid gap-4"
      :submit-attrs="{
        inputClass: 'hidden',
        wrapperClass: 'hidden',
      }"
      :incomplete-message="false"
      @submit="submitForm"
    >
      <div v-if="isBitrix && bridgeTokenItem" class="flex flex-col gap-2">
        <FormKit v-bind="bridgeTokenItem" />
        <div class="flex items-center gap-2 -mt-2">
          <NextButton
            type="button"
            ghost
            sm
            slate
            :label="$t('INTEGRATION_APPS.BITRIX.TOKEN.GENERATE')"
            @click="generateBridgeToken"
          />
          <NextButton
            type="button"
            ghost
            sm
            slate
            icon="i-lucide-copy"
            @click="copyBridgeToken"
          />
        </div>
        <p class="text-xs text-n-amber-11">
          {{ $t('INTEGRATION_APPS.BITRIX.TOKEN.WARNING') }}
        </p>
      </div>
      <template v-if="isNemesis">
        <section
          v-for="section in nemesisSections"
          :key="section.title"
          class="flex flex-col gap-2 pt-3 border-t border-n-weak"
        >
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ section.title }}
          </h4>
          <template v-for="item in section.items" :key="item.name">
            <div
              v-if="item.name === 'bridge_token'"
              class="flex flex-col gap-2"
            >
              <template v-if="isEditing && !values.bridge_token">
                <p class="text-sm text-n-slate-11">
                  {{ $t('INTEGRATION_APPS.NEMESIS.TOKEN.HIDDEN') }}
                </p>
                <div>
                  <NextButton
                    type="button"
                    faded
                    slate
                    :label="$t('INTEGRATION_APPS.NEMESIS.TOKEN.GENERATE_NEW')"
                    @click="generateBridgeToken"
                  />
                </div>
              </template>
              <template v-else>
                <FormKit v-bind="item" />
                <div class="flex items-center gap-2 -mt-2">
                  <NextButton
                    type="button"
                    ghost
                    sm
                    slate
                    :label="$t('INTEGRATION_APPS.NEMESIS.TOKEN.GENERATE')"
                    @click="generateBridgeToken"
                  />
                  <NextButton
                    type="button"
                    ghost
                    sm
                    slate
                    icon="i-lucide-copy"
                    @click="copyBridgeToken"
                  />
                </div>
                <p class="text-xs text-n-amber-11">
                  {{
                    isEditing
                      ? $t('INTEGRATION_APPS.NEMESIS.TOKEN.WARNING_EDIT')
                      : $t('INTEGRATION_APPS.NEMESIS.TOKEN.WARNING')
                  }}
                </p>
              </template>
            </div>
            <FormKit v-else v-bind="item" />
          </template>
        </section>
      </template>
      <div v-if="isNemesis" class="flex flex-col gap-4">
        <section
          v-for="group in nemesisOptionalGroups"
          v-show="isGroupOpen(group.id)"
          :key="group.id"
          :data-group="group.id"
          :style="{ order: openGroups.indexOf(group.id) }"
          class="flex flex-col gap-2 pt-3 border-t border-n-weak"
        >
          <div class="flex items-center justify-between gap-2">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ $t(`INTEGRATION_APPS.NEMESIS.OPTIONAL.${group.i18n}.TITLE`) }}
            </h4>
            <NextButton
              type="button"
              ghost
              sm
              slate
              icon="i-lucide-x"
              :label="$t('INTEGRATION_APPS.NEMESIS.OPTIONAL.REMOVE')"
              @click="closeGroup(group.id)"
            />
          </div>
          <p class="text-xs text-n-slate-11">
            {{ $t(`INTEGRATION_APPS.NEMESIS.OPTIONAL.${group.i18n}.HINT`) }}
          </p>
          <FormKit v-for="item in group.items" :key="item.name" v-bind="item" />
        </section>
      </div>
      <div
        v-if="isNemesis && nemesisClosedGroups.length"
        data-test="more-options"
        class="flex flex-col gap-4 pt-3 border-t border-n-weak"
      >
        <div class="flex flex-col gap-1">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ $t('INTEGRATION_APPS.NEMESIS.OPTIONAL.TITLE') }}
          </h4>
          <p class="text-xs text-n-slate-11">
            {{ $t('INTEGRATION_APPS.NEMESIS.OPTIONAL.INTRO') }}
          </p>
        </div>
        <div
          v-for="section in nemesisClosedSections"
          :key="section.key"
          class="flex flex-col gap-2"
        >
          <h5 class="text-xs font-medium uppercase text-n-slate-11">
            {{
              $t(`INTEGRATION_APPS.NEMESIS.OPTIONAL.SECTIONS.${section.key}`)
            }}
          </h5>
          <div
            v-for="group in section.groups"
            :key="group.id"
            class="flex items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-3"
          >
            <div class="flex items-start gap-3 min-w-0">
              <i
                class="w-5 h-5 mt-0.5 text-n-slate-10 shrink-0"
                :class="[group.icon]"
              />
              <div class="flex flex-col gap-0.5 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      $t(
                        `INTEGRATION_APPS.NEMESIS.OPTIONAL.${group.i18n}.TITLE`
                      )
                    }}
                  </span>
                  <span
                    v-if="group.recommended"
                    class="text-xs rounded-md bg-n-teal-3 text-n-teal-11 px-1.5 py-0.5"
                  >
                    {{ $t('INTEGRATION_APPS.NEMESIS.OPTIONAL.RECOMMENDED') }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-11">
                  {{
                    $t(
                      `INTEGRATION_APPS.NEMESIS.OPTIONAL.${group.i18n}.DESCRIPTION`
                    )
                  }}
                </p>
              </div>
            </div>
            <NextButton
              type="button"
              faded
              slate
              sm
              icon="i-lucide-plus"
              :data-add-group="group.id"
              :label="$t('INTEGRATION_APPS.NEMESIS.OPTIONAL.ADD')"
              @click="openGroup(group.id)"
            />
          </div>
        </div>
      </div>
      <FormKit
        v-for="item in isNemesis ? [] : visibleFormItems"
        :key="item.name"
        v-bind="item"
      />
      <BitrixSurveyButtonsConfig
        v-if="showBitrixSurveyWidget"
        v-model:survey-buttons="surveyButtonRows"
        :values="values"
        :template="selectedBitrixTemplate"
        :entity-field="bitrixEntityField"
        :status-field-field="bitrixStatusFieldField"
      />
      <p
        v-else-if="isBitrix"
        class="text-xs text-n-slate-11 p-3 rounded-lg outline outline-1 outline-n-weak"
      >
        {{ $t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.HIDDEN_HINT') }}
      </p>
      <FormKit
        v-if="isHookTypeInbox"
        :options="inboxes"
        type="select"
        name="inbox"
        input-class="reset-base"
        :placeholder="$t('INTEGRATION_APPS.ADD.FORM.INBOX.LABEL')"
        :label="$t('INTEGRATION_APPS.ADD.FORM.INBOX.PLACEHOLDER')"
        validation="required"
        validation-name="Inbox"
      />
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="submitButtonLabel"
          :is-loading="isSubmitting"
        />
      </div>
    </FormKit>
  </div>
</template>

<style lang="css">
.formkit-outer {
  @apply mt-2;
}

.formkit-form > .formkit-wrapper > ul.formkit-messages {
  @apply hidden;
}

.formkit-form .formkit-help {
  @apply text-n-slate-10 text-sm font-normal mt-2 w-full;
}

/* equivalent of .reset-base */
.formkit-input {
  margin-bottom: 0px !important;
}

[data-invalid] .formkit-message {
  @apply text-n-ruby-9 block text-xs font-normal my-1 w-full;
}

.formkit-outer[data-type='checkbox'] .formkit-wrapper {
  @apply flex items-center gap-2 px-0.5;
}

.formkit-messages {
  @apply list-none m-0 p-0;
}

.formkit-actions {
  @apply hidden;
}
</style>
